import React, { useEffect, useRef, useState } from 'react';
import moment, { Moment } from 'moment';
import {
  Box,
  GlobalStyles,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import { CalendarToday, Clear } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import type { DateRange as DateRangeType } from '../../types/common/publicSchedule.types';
import colors from '../../common/colors';
import {
  getDefaultFilters,
  applyRangeStyles,
  formatDateRange,
  toIsoOrNull,
} from './utils';
import { useClickOutside, useNarrowScreen } from '../../hooks';
import { useRegisterFilterActivity } from '../FilterDrawer/FilterActivityContext';
import DesktopView from './components/DesktopView';
import MobileView from './components/MobileView';
import type { DateRange, MobileStep, CalendarView } from './types';
import {
  DateRangeTrackingEvent,
  type DateRangeTrackingEventType,
  type DateRangeTrackingEventData,
} from './types';

interface DateRangeFilterProps {
  startDate: string | null;
  endDate: string | null;
  onChange: (range: DateRangeType) => void;
  label?: string;
  /**
   * When true, the picker always renders the step-based MobileView regardless
   * of viewport width. Used when the picker is hosted inside a narrow
   * container (e.g. the filter drawer) where the dual-calendar DesktopView
   * would overflow.
   */
  forceNarrow?: boolean;
  /**
   * When provided, this filter self-reports its active state to the
   * surrounding `FilterDrawerContainer`'s activity registry.
   *
   * The default auto-derived active state is "either start or end is set".
   * Host pages that treat an auto-applied default window (e.g. today →
   * today + 14d) as *inactive* should pass an explicit `isActive` override.
   */
  filterId?: string;
  /** Optional override for the auto-derived active state. */
  isActive?: boolean;
  /**
   * Optional analytics hook. Fires at three meaningful moments:
   *   - `selected`            — user clicks a date (start, end, or mobile step)
   *   - `quickFilterApplied`  — user clicks a quick-select preset
   *   - `applied`             — user clicks the Apply button
   * Host pages wire this to `trackEvent(...)` calls.
   */
  onTrackingEvent?: (
    type: DateRangeTrackingEventType,
    data: DateRangeTrackingEventData
  ) => void;
}

const globalStyles = {
  '.MuiPickersDay-root:active': {
    backgroundColor: `${colors.grey_300} !important`,
    color: 'white !important',
    borderRadius: '50% !important',
  },
  '.MuiDialogActions-root.MuiPickersLayout-actionBar': {
    display: 'none !important',
  },
  '.MuiPickersLayout-root': {
    maxWidth: '100% !important',
    overflow: 'hidden !important',
  },
  '.MuiDayCalendar-root': {
    maxWidth: '100% !important',
  },
  '.MuiPickersCalendarHeader-root': {
    maxWidth: '100% !important',
  },
};

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  startDate,
  endDate,
  onChange,
  label = 'Date Range',
  forceNarrow = false,
  filterId = 'dateRange',
  isActive,
  onTrackingEvent,
}) => {
  useRegisterFilterActivity(
    filterId,
    isActive ?? (startDate !== null && endDate !== null)
  );

  const viewportNarrow = useNarrowScreen();
  const isNarrowScreen = viewportNarrow || forceNarrow;
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Current displayed values in the calendar (not yet applied)
  const [dateRange, setDateRange] = useState<DateRange>([
    startDate ? moment(startDate) : null,
    endDate ? moment(endDate) : null,
  ]);

  // Previously applied values (for Cancel functionality)
  const [appliedDateRange, setAppliedDateRange] = useState<DateRange>([
    startDate ? moment(startDate) : null,
    endDate ? moment(endDate) : null,
  ]);

  const [mobileStep, setMobileStep] = useState<MobileStep>('start');
  const [currentView, setCurrentView] = useState<CalendarView>('day');
  const [hoveredDate, setHoveredDate] = useState<Moment | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);

  const filters = getDefaultFilters();

  const wrapperRef = useRef<HTMLElement>(null);

  // Sync with prop changes
  useEffect(() => {
    const newRange: DateRange = [
      startDate ? moment(startDate) : null,
      endDate ? moment(endDate) : null,
    ];
    setDateRange(newRange);
    setAppliedDateRange(newRange);
  }, [startDate, endDate]);

  const closeCalendar = () => {
    setIsCalendarOpen(false);
    setHoveredDate(null);
  };

  // Close without applying - revert local and parent state to last confirmed values
  const handleClickOutside = () => {
    // Only call onChange if the current dateRange differs from appliedDateRange
    const hasChanged =
      dateRange[0]?.valueOf() !== appliedDateRange[0]?.valueOf() ||
      dateRange[1]?.valueOf() !== appliedDateRange[1]?.valueOf();

    setDateRange(appliedDateRange);
    closeCalendar();

    if (hasChanged) {
      onChange({
        startDate: appliedDateRange[0]
          ? appliedDateRange[0].startOf('day').toISOString()
          : null,
        endDate: appliedDateRange[1]
          ? appliedDateRange[1].endOf('day').toISOString()
          : null,
      });
    }
  };

  useClickOutside(wrapperRef, handleClickOutside);

  const handleQuickSelect = (filterKey: string) => {
    const filter = filters.find((filter) => filter.key === filterKey);
    if (!filter) return;

    const newRange = filter.getDateRange();
    setDateRange(newRange);
    setSelectedFilter(filterKey);

    const isoStart = toIsoOrNull(newRange[0], 'start');
    const isoEnd = toIsoOrNull(newRange[1], 'end');

    // Immediately notify parent so draft state updates in the drawer
    onChange({ startDate: isoStart, endDate: isoEnd });

    onTrackingEvent?.(DateRangeTrackingEvent.QUICK_FILTER_APPLIED, {
      startDate: isoStart,
      endDate: isoEnd,
      filterKey,
    });
  };

  useEffect(() => {
    if (!isCalendarOpen) return;

    const timer = setTimeout(() => {
      applyRangeStyles(dateRange, hoveredDate, isNarrowScreen);
    }, 40);

    return () => clearTimeout(timer);
  }, [dateRange, hoveredDate, isCalendarOpen, isNarrowScreen]);

  const handleInputClick = () => {
    setIsCalendarOpen((prev) => {
      const opening = !prev;
      if (opening && isNarrowScreen) {
        setMobileStep('start');
      }
      return opening;
    });
  };

  const handleClear = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();

    const emptyRange: DateRange = [null, null];
    setDateRange(emptyRange);
    setAppliedDateRange(emptyRange);
    setIsCalendarOpen(false);
    onChange({ startDate: null, endDate: null });
    onTrackingEvent?.(DateRangeTrackingEvent.CLEARED, {
      startDate: null,
      endDate: null,
    });
  };

  const handleCancel = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setDateRange(appliedDateRange);
    closeCalendar();
  };

  const applyRange = (rangeToApply: DateRange) => {
    setDateRange(rangeToApply);
    setAppliedDateRange(rangeToApply);
    closeCalendar();
    const isoStart = toIsoOrNull(rangeToApply[0], 'start');
    const isoEnd = toIsoOrNull(rangeToApply[1], 'end');
    onChange({ startDate: isoStart, endDate: isoEnd });
  };

  const handleApply = () => {
    applyRange(dateRange);
    onTrackingEvent?.(DateRangeTrackingEvent.APPLIED, {
      startDate: toIsoOrNull(dateRange[0], 'start'),
      endDate: toIsoOrNull(dateRange[1], 'end'),
    });
  };

  const handleStartDateChange = (newValue: Moment | null) => {
    const isEndInvalid =
      newValue && dateRange[1] && newValue.isAfter(dateRange[1], 'day');
    setDateRange([newValue, isEndInvalid ? null : dateRange[1]]);
  };

  const handleEndDateChange = (newValue: Moment | null) => {
    setDateRange([dateRange[0], newValue]);
  };

  const handleMobileDateChange = (newValue: Moment | null) => {
    // Determine next range based on current step
    let nextRange: DateRange;
    if (mobileStep === 'start') {
      const isEndInvalid =
        newValue && dateRange[1] && newValue.isAfter(dateRange[1], 'day');
      nextRange = [newValue, isEndInvalid ? null : dateRange[1]];
    } else {
      nextRange = [dateRange[0], newValue];
    }

    // Update local state
    setDateRange(nextRange);

    // Only notify parent if BOTH dates are set (complete range)
    // This prevents incomplete ranges from being saved when drawer Save is clicked
    if (nextRange[0] && nextRange[1]) {
      onChange({
        startDate: nextRange[0].startOf('day').toISOString(),
        endDate: nextRange[1].endOf('day').toISOString(),
      });
    }

    // Advance to end step after selecting start date in day view
    if (mobileStep === 'start' && currentView === 'day') {
      setMobileStep('end');
    }
  };

  const handleDayMouseEnter = (event: MouseEvent) => {
    if (isNarrowScreen || !dateRange[0]) return;

    const dayElement = event.target as HTMLElement;
    if (!(dayElement instanceof HTMLElement)) return;
    const dayText = dayElement.textContent;
    if (!dayText) return;

    const dayNumber = parseInt(dayText, 10);
    if (isNaN(dayNumber)) return;

    const calendarContainer = dayElement.closest('[data-calendar-type]');
    if (!(calendarContainer instanceof HTMLElement)) return;

    const monthYearElement = calendarContainer.querySelector(
      '.MuiPickersCalendarHeader-label'
    );
    if (!monthYearElement?.textContent) return;

    const parts = monthYearElement.textContent.trim().split(' ');
    if (parts.length !== 2) return;

    const monthName = parts[0];
    const year = parseInt(parts[1], 10);

    const testDate = moment(`${monthName} ${year}`, 'MMMM YYYY');
    if (!testDate.isValid()) return;

    const hoveredDay = moment()
      .year(testDate.year())
      .month(testDate.month())
      .date(dayNumber);

    setHoveredDate(hoveredDay);
  };

  const handleDayMouseLeave = () => {
    if (!isNarrowScreen) {
      setHoveredDate(null);
    }
  };

  useEffect(() => {
    if (!isCalendarOpen) return;

    const addEventListeners = () => {
      document.querySelectorAll('.MuiPickersDay-root').forEach((day) => {
        day.addEventListener(
          'mouseenter',
          handleDayMouseEnter as EventListener
        );
        day.addEventListener('mouseleave', handleDayMouseLeave);
      });
    };

    const timer = setTimeout(addEventListeners, 100);

    return () => {
      clearTimeout(timer);
      document.querySelectorAll('.MuiPickersDay-root').forEach((day) => {
        day.removeEventListener(
          'mouseenter',
          handleDayMouseEnter as EventListener
        );
        day.removeEventListener('mouseleave', handleDayMouseLeave);
      });
    };
  }, [isCalendarOpen, dateRange]);

  const getDisplayValue = (): string => {
    return formatDateRange(dateRange[0], dateRange[1]);
  };

  const hasSelectedDates = dateRange[0] || dateRange[1];
  // Disabled when exactly one date is set — the range is incomplete
  const isApplyDisabled = !!dateRange[0] !== !!dateRange[1];

  const handleClearButtonClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (hasSelectedDates) {
      handleClear(e);
    } else {
      handleInputClick();
    }
  };

  return (
    <>
      <GlobalStyles styles={globalStyles} />
      <Box
        ref={wrapperRef}
        display="flex"
        flexDirection="column"
        sx={{ position: 'relative', width: '100%' }}
      >
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <TextField
            placeholder="Date Range"
            variant="filled"
            label={label}
            onClick={handleInputClick}
            value={getDisplayValue()}
            slotProps={{
              input: {
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleClearButtonClick}
                      edge="end"
                      size="small"
                    >
                      {hasSelectedDates ? <Clear /> : <CalendarToday />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              cursor: 'pointer',
              width: '100%',
              '& .MuiOutlinedInput-root': {
                height: '48px',
              },
              '& .MuiInputBase-input': {
                cursor: 'pointer',
              },
            }}
          />

          {isCalendarOpen && !isNarrowScreen && (
            <DesktopView
              selectedFilter={selectedFilter}
              handleQuickSelect={handleQuickSelect}
              filters={filters}
              dateRange={dateRange}
              handleStartDateChange={handleStartDateChange}
              handleEndDateChange={handleEndDateChange}
              handleCancel={handleCancel}
              handleApply={handleApply}
              isApplyDisabled={isApplyDisabled}
            />
          )}

          {isCalendarOpen && isNarrowScreen && (
            <MobileView
              selectedFilter={selectedFilter}
              handleQuickSelect={handleQuickSelect}
              filters={filters}
              dateRange={dateRange}
              handleMobileDateChange={handleMobileDateChange}
              mobileStep={mobileStep}
              setMobileStep={setMobileStep}
              setCurrentView={setCurrentView}
            />
          )}
        </LocalizationProvider>
      </Box>
    </>
  );
};

export default DateRangeFilter;
